/**
 * The list of sermons: who prepares one this week, and who preached when.
 *
 * **Rewritten by C-002** (`FND-1591`, 2026-10-06, `docs/specs/features/C-002/spec.md` §3.5). Until
 * then this was F-025's port of `legacy/frontend/src/screens/SermonsScreen.tsx`, the rota of one
 * brother per date. The owner brought back what the choir did before the app: three brothers
 * prepare a sermon, and whoever of them comes to the rehearsal preaches. The list is the server's,
 * computed on every read — the head of the queue, the keeper's pins first, this week's «не смогу»
 * skipped — so nothing here decides who is on it.
 *
 * Kept from the port: the load and its refusal (`FND-940`: the text is set and read by nothing,
 * `Failed` takes none), the re-read on waking, `edit()` around every write, a refusal drawn where
 * the tap was, and the empty state's two sentences.
 *
 * The screen is read by the whole choir and edited by two people: a regent, and the brother
 * appointed to keep it. `can_edit` and `can_mark` decide that on the server, so the screen never
 * draws a control the server would refuse.
 */

import { useCallback, useEffect, useState } from 'react';

import type { BrotherRow, SermonRota, SermonRow } from '@contracts/index';
import { ApiError, api } from '../api';
import { formatDayMonth, formatTime, formatWeekdayDate } from '../format';
import { useWake } from '../liveness';
import { useBackKey } from '../backKey';
import SermonMark from './SermonMark';
import { edit, type Refusal } from './sermonEdit';
import Sheet from './Sheet';
import { Empty, Failed, Settling, Skeleton } from './States';

/** «ещё не проповедовал» or «последняя — 21.08», and «добавлен вручную» for a pin. */
function brotherSub(one: BrotherRow): string {
  const last =
    one.last_done === null ? 'ещё не проповедовал' : `последняя — ${formatDayMonth(one.last_done)}`;
  return one.pinned ? `${last} · добавлен вручную` : last;
}

/** What a rehearsal's mark came to, as the name line of its row. */
function preacherLine(row: SermonRow): string {
  if (row.status === 'done') return row.display_name ?? '';
  return row.status === 'missed' ? 'Проповеди не было' : 'Не отмечено';
}

export default function SermonsScreen({ onBack }: { onBack: () => void }) {
  const [rota, setRota] = useState<SermonRota | null>(null);
  const [failure, setFailure] = useState<Refusal | null>(null);
  const [busy, setBusy] = useState(false);
  /** The past rehearsal whose mark is open, by session id. */
  const [marking, setMarking] = useState<string | null>(null);
  /** Whether the sheet of brothers to add is open. */
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      setRota(await api.sermonRota());
      setFailure(null);
    } catch (error) {
      setFailure({
        at: 'screen',
        text: error instanceof ApiError ? error.message : 'Не удалось загрузить график',
      });
    }
  }, []);

  useEffect(() => {
    // The load on mount is the screen's one synchronisation with the server, kept as the port
    // had it; `load` sets state only once the answer is back.
    // oxlint-disable-next-line react/set-state-in-effect
    void load();
  }, [load]);

  useBackKey(onBack);

  // Re-read on waking, like the schedule: a brother who said «не смогу» while
  // the phone was in a pocket is exactly what the keeper came back to see.
  useWake(() => void load());

  function refusal(at: string) {
    return failure !== null && failure.at === at ? <p className="failure">{failure.text}</p> : null;
  }

  /** Whether the edit went through, which is what decides a sheet's fate. */
  async function act(action: () => Promise<SermonRota>, at: string): Promise<boolean> {
    setBusy(true);
    setFailure(null);
    const answer = await edit(at, action);
    if (answer.rota !== null) setRota(answer.rota);
    setFailure(answer.refusal);
    setBusy(false);
    return answer.settled;
  }

  function mark(row: SermonRow, memberId: number | null): void {
    void act(() => api.markSermon(row.session_id, memberId), row.session_id).then((settled) => {
      // On success only: the sheet is where the tap was and where the refusal is drawn.
      if (settled && marking === row.session_id) setMarking(null);
    });
  }

  function markFor(row: SermonRow) {
    return (
      <SermonMark
        picked={row.status}
        preacher={row.member_id}
        preparing={rota?.preparing ?? []}
        brothers={rota?.brothers ?? []}
        busy={busy}
        onMark={(memberId) => mark(row, memberId)}
      />
    );
  }

  const today = rota?.today ?? null;
  const opened = rota?.past.find((row) => row.session_id === marking) ?? null;
  const onList = new Set(rota?.preparing.map((one) => one.member_id) ?? []);
  const addable = (rota?.brothers ?? []).filter((one) => !onList.has(one.member_id));

  return (
    <>
      <div className="screen-head">
        <h1 className="screen-title">Проповеди</h1>
      </div>
      <p className="hint">
        Готовят четверо — те, кто дольше всех не проповедовал. Проповедует тот из них, кто придёт на
        спевку.
        {rota !== null && rota.can_edit
          ? ' После спевки отметь, кто проповедовал.'
          : ' Отмечает назначенный брат.'}
      </p>

      {/* One or the other, and neither once the list is on screen: a reload
          refused after an edit leaves it standing. */}
      {rota === null && failure === null && <Skeleton />}
      {rota === null && failure?.at === 'screen' && (
        <Failed subject="sermons" onRetry={() => void load()} />
      )}

      {today !== null && (
        <Settling>
          <div className="block">
            <span className="label">Сегодня</span>
            <div className="cards">
              <section className="card card--gathering card--live">
                <p className="date-line date-line--soon">
                  {formatWeekdayDate(today.start)} ·{' '}
                  <span className="num">{formatTime(today.start)}</span>
                </p>
                <h2 className="card-title">
                  {today.status === 'planned' ? 'Кто проповедовал?' : preacherLine(today)}
                </h2>
                {today.can_mark && markFor(today)}
                {refusal(today.session_id)}
              </section>
            </div>
          </div>
        </Settling>
      )}

      {rota !== null && rota.brothers.length > 0 && (
        <Settling>
          <div className="block">
            <span className="label">Готовят проповедь</span>
            <ul className="live">
              {rota.preparing.map((one) => (
                <li key={one.member_id} className="live-row">
                  <span className="live-body">
                    <span className="live-name">{one.display_name}</span>
                    <span className="live-sub">{brotherSub(one)}</span>
                  </span>
                  {rota.can_edit && (
                    <button
                      className="live-mark"
                      data-press="wash"
                      disabled={busy}
                      onClick={() => void act(() => api.unpinSermon(one.member_id), 'list')}
                    >
                      убрать до понедельника
                    </button>
                  )}
                </li>
              ))}
            </ul>
            {rota.out.length > 0 && (
              <p className="hint">
                Не смогут на этой неделе: {rota.out.map((one) => one.display_name).join(', ')}.
              </p>
            )}
            {rota.can_edit && addable.length > 0 && (
              <div className="row-buttons">
                <button
                  className="btn btn--outline"
                  disabled={busy}
                  onClick={() => setAdding(true)}
                >
                  Добавить брата
                </button>
              </div>
            )}
            {!adding && refusal('list')}
          </div>
        </Settling>
      )}

      {rota !== null && rota.past.length > 0 && (
        <Settling>
          <div className="block">
            <span className="label">Что прошло</span>
            <ul className="live">
              {rota.past.map((row) => (
                <li
                  key={row.session_id}
                  className={row.status === 'done' ? 'live-row' : 'live-row live-row--waiting'}
                >
                  <span className="live-body">
                    <span className="live-name">{preacherLine(row)}</span>
                    <span className="live-sub">
                      {formatWeekdayDate(row.start)} · {formatTime(row.start)}
                    </span>
                  </span>
                  {row.can_mark && (
                    <button
                      className="live-mark"
                      data-press="wash"
                      disabled={busy}
                      onClick={() => setMarking(row.session_id)}
                    >
                      {row.status === 'planned' ? 'отметить' : 'изменить'}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </Settling>
      )}

      {rota !== null &&
        rota.brothers.length === 0 &&
        rota.today === null &&
        rota.past.length === 0 && (
          <Empty
            fact="Проповедей пока нет"
            note="Ставить пока некого: очередь собирается из братьев, а пол человека записывает регент в его карточке."
          />
        )}

      {/* The mark of a past rehearsal, as a sheet: choosing rather than
          looking, and the container rule of 2026-08-21 gives choosing a sheet. */}
      <Sheet open={opened !== null} label="Кто проповедовал?" onClose={() => setMarking(null)}>
        {opened !== null && markFor(opened)}
        {opened !== null && refusal(opened.session_id)}
      </Sheet>

      {/* Every brother not on the list, each with when he last preached — the
          figure the queue is ordered by, so adding somebody past it is a
          decision made against the same figure the app made its own with. */}
      <Sheet open={adding} label="Добавить брата" onClose={() => setAdding(false)}>
        <ul className="live">
          {addable.map((one) => (
            <li key={one.member_id} className="live-row">
              <span className="live-body">
                <span className="live-name">{one.display_name}</span>
                <span className="live-sub">{brotherSub(one)}</span>
              </span>
              <button
                className="live-mark"
                data-press="wash"
                disabled={busy}
                onClick={() =>
                  void act(() => api.pinSermon(one.member_id), 'list').then((settled) => {
                    if (settled) setAdding(false);
                  })
                }
              >
                добавить
              </button>
            </li>
          ))}
        </ul>
        {adding && refusal('list')}
      </Sheet>
    </>
  );
}
